/**
 * Sector operations — Phone items catalog, resolver, and legacy grants.
 * R39 A14.a (plan docs/plans/sector-operations.md §2 SO-D24, SO-D298).
 *
 * NOTE FOR MEMBERS (Editor, Viewer):
 * The phone item gates their phone tab only (A14.b). It never widens
 * what their role already reads on the desk.
 */

import type { WorkspaceRole } from '../workspaceRoles.js'

export type OpsPhoneSurface = 'HELPER' | 'MEMBER'

export type OpsPhoneItemKey =
  | 'h.checkIn'
  | 'h.dueChecks'
  | 'h.dpr'
  | 'h.roll'
  | 'h.request'
  | 'h.roomClean'
  | 'h.foundItem'
  | 'h.bookingCheckIn'
  | 'h.bmw'
  | 'h.oncall'
  | 'h.hindrance'
  | 'h.plant'
  | 'm.approvals'
  | 'm.roster'
  | 'm.registers'
  | 'm.reports'
  | 'm.requests'
  | 'm.exceptions'
  | 'm.equipment'
  | 'm.beds'
  | 'm.bmw'
  | 'm.oncall'
  | 'm.dpr'
  | 'm.inspections'
  | 'm.plant'

export interface OpsPhoneItem {
  key: OpsPhoneItemKey
  surface: OpsPhoneSurface
  labelKey: string
  descriptionKey: string
  anyOfModules: readonly string[]
  allowedRoles: readonly WorkspaceRole[]
  recommendedRoles: readonly WorkspaceRole[]
  unlockedCallables?: readonly string[]
}

export interface OpsPhoneAccessDoc {
  workspaceId: string
  roleAllow: Partial<Record<WorkspaceRole, OpsPhoneItemKey[]>>
  updatedBy: string
  updatedAt: unknown
  source?: 'LEGACY' | 'MODULE_ON' | 'ADMIN'
}

export interface OpsPhoneAccessPersonDoc {
  workspaceId: string
  uid: string
  allow: OpsPhoneItemKey[]
  deny: OpsPhoneItemKey[]
  updatedBy: string
  updatedAt: unknown
}

const ALL_OPS_SECTOR_MODULES = [
  'constructionEnabled',
  'hospitalEnabled',
  'sitesEnabled',
  'deliveryEnabled',
  'shopsEnabled',
  'staysEnabled',
  'plantEnabled',
] as const

const REQUESTS_ENABLED_SECTOR_MODULES = [
  'constructionEnabled',
  'hospitalEnabled',
  'sitesEnabled',
  'staysEnabled',
  'plantEnabled',
] as const

// Modules whose sector has assets on in OPS_SECTORS (construction, hospital, shops, stays, jobs, plant)
const ASSET_ENABLED_SECTOR_MODULES = [
  'constructionEnabled',
  'hospitalEnabled',
  'shopsEnabled',
  'staysEnabled',
  'jobsEnabled',
  'plantEnabled',
] as const

export const OPS_PHONE_ITEMS: Record<OpsPhoneItemKey, OpsPhoneItem> = {
  'h.checkIn': {
    key: 'h.checkIn',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemCheckIn',
    descriptionKey: 'NLSOpsPhoneItemCheckInDesc',
    anyOfModules: ['shopsEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: [
      'checkInByPlaceQr',
      'proxyMarkByColleague',
      'getPlaceScanContext',
    ],
  },
  'h.dueChecks': {
    key: 'h.dueChecks',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemDueChecks',
    descriptionKey: 'NLSOpsPhoneItemDueChecksDesc',
    anyOfModules: ALL_OPS_SECTOR_MODULES,
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['recordCheckpointScan', 'skipDue'],
  },
  'h.dpr': {
    key: 'h.dpr',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemDpr',
    descriptionKey: 'NLSOpsPhoneItemDprDesc',
    anyOfModules: ['constructionEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: [],
  },
  'h.hindrance': {
    key: 'h.hindrance',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemHindrance',
    descriptionKey: 'NLSOpsPhoneItemHindranceDesc',
    anyOfModules: ['constructionEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['createConIssue'],
  },
  'h.roll': {
    key: 'h.roll',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemRoll',
    descriptionKey: 'NLSOpsPhoneItemRollDesc',
    anyOfModules: ALL_OPS_SECTOR_MODULES,
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['markAttendanceRoll'],
  },
  'h.request': {
    key: 'h.request',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemRequest',
    descriptionKey: 'NLSOpsPhoneItemRequestDesc',
    anyOfModules: REQUESTS_ENABLED_SECTOR_MODULES,
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['raiseRequest'],
  },
  'h.roomClean': {
    key: 'h.roomClean',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemRoomClean',
    descriptionKey: 'NLSOpsPhoneItemRoomCleanDesc',
    anyOfModules: ['staysEnabled', 'hospitalEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: [
      'prepareRoomCleanRecord',
      'checkOutRoom',
      'markRoomOccupied',
      'setRoomDueOut',
      'startRoomClean',
      'pauseRoomClean',
      'resumeRoomClean',
      'markRoomCleaned',
      'inspectRoom',
      'setRoomService',
      'setRoomTags',
      'setPropertyMinibarCheck',
      'assignRooms',
      'unassignRoom',
      'reassignRoom',
      'transitionRoomState',
    ],
  },
  'h.foundItem': {
    key: 'h.foundItem',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemFoundItem',
    descriptionKey: 'NLSOpsPhoneItemFoundItemDesc',
    anyOfModules: ['staysEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['logFoundItem'],
  },
  'h.bookingCheckIn': {
    key: 'h.bookingCheckIn',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemBookingCheckIn',
    descriptionKey: 'NLSOpsPhoneItemBookingCheckInDesc',
    anyOfModules: ['staysEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['bookingCheckInByPlaceQr'],
  },
  'm.approvals': {
    key: 'm.approvals',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemApprovals',
    descriptionKey: 'NLSOpsPhoneItemApprovalsDesc',
    anyOfModules: ALL_OPS_SECTOR_MODULES,
    allowedRoles: ['ADMIN', 'EDITOR'],
    recommendedRoles: ['ADMIN', 'EDITOR'],
  },
  'm.roster': {
    key: 'm.roster',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemRoster',
    descriptionKey: 'NLSOpsPhoneItemRosterDesc',
    anyOfModules: ALL_OPS_SECTOR_MODULES,
    allowedRoles: ['ADMIN', 'EDITOR', 'VIEWER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'VIEWER'],
  },
  'm.registers': {
    key: 'm.registers',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemRegisters',
    descriptionKey: 'NLSOpsPhoneItemRegistersDesc',
    anyOfModules: ALL_OPS_SECTOR_MODULES,
    allowedRoles: ['ADMIN', 'EDITOR', 'VIEWER'],
    recommendedRoles: ['ADMIN', 'EDITOR'],
  },
  'm.reports': {
    key: 'm.reports',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemReports',
    descriptionKey: 'NLSOpsPhoneItemReportsDesc',
    anyOfModules: ALL_OPS_SECTOR_MODULES,
    allowedRoles: ['ADMIN', 'EDITOR', 'VIEWER'],
    recommendedRoles: ['ADMIN', 'EDITOR'],
  },
  'm.requests': {
    key: 'm.requests',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemRequests',
    descriptionKey: 'NLSOpsPhoneItemRequestsDesc',
    anyOfModules: REQUESTS_ENABLED_SECTOR_MODULES,
    allowedRoles: ['ADMIN', 'EDITOR', 'VIEWER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'VIEWER'],
  },
  'm.exceptions': {
    key: 'm.exceptions',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemExceptions',
    descriptionKey: 'NLSOpsPhoneItemExceptionsDesc',
    anyOfModules: ['hospitalEnabled'],
    allowedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
  },
  'm.equipment': {
    key: 'm.equipment',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemEquipment',
    descriptionKey: 'NLSOpsPhoneItemEquipmentDesc',
    anyOfModules: ASSET_ENABLED_SECTOR_MODULES,
    allowedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
  },
  'm.beds': {
    key: 'm.beds',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemBeds',
    descriptionKey: 'NLSOpsPhoneItemBedsDesc',
    anyOfModules: ['hospitalEnabled'],
    allowedRoles: ['ADMIN', 'EDITOR', 'VIEWER', 'OWNER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
  },
  'h.bmw': {
    key: 'h.bmw',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemBmw',
    descriptionKey: 'NLSOpsPhoneItemBmwDesc',
    anyOfModules: ['hospitalEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['saveBmwDay', 'submitBmwDay', 'getBmwStatus'],
  },
  'm.bmw': {
    key: 'm.bmw',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemMemberBmw',
    descriptionKey: 'NLSOpsPhoneItemMemberBmwDesc',
    anyOfModules: ['hospitalEnabled'],
    allowedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
    unlockedCallables: ['saveBmwDay', 'submitBmwDay', 'correctBmwDay', 'getBmwStatus'],
  },
  'h.oncall': {
    key: 'h.oncall',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemOnCall',
    descriptionKey: 'NLSOpsPhoneItemOnCallDesc',
    anyOfModules: ['hospitalEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['listMyOnCallSlots', 'acknowledgeOnCall'],
  },
  'm.oncall': {
    key: 'm.oncall',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemMemberOnCall',
    descriptionKey: 'NLSOpsPhoneItemMemberOnCallDesc',
    anyOfModules: ['hospitalEnabled'],
    allowedRoles: ['ADMIN', 'EDITOR', 'VIEWER', 'OWNER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'VIEWER', 'OWNER'],
    unlockedCallables: ['listMyOnCallSlots', 'acknowledgeOnCall'],
  },
  'm.dpr': {
    key: 'm.dpr',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemDpr',
    descriptionKey: 'NLSOpsPhoneItemDprDesc',
    anyOfModules: ['constructionEnabled'],
    allowedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
  },
  'm.inspections': {
    key: 'm.inspections',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemInspections',
    descriptionKey: 'NLSOpsPhoneItemInspectionsDesc',
    anyOfModules: ['constructionEnabled'],
    allowedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'OWNER'],
  },
  'h.plant': {
    key: 'h.plant',
    surface: 'HELPER',
    labelKey: 'NLSOpsPhoneItemPlant',
    descriptionKey: 'NLSOpsPhoneItemPlantDesc',
    anyOfModules: ['plantEnabled'],
    allowedRoles: ['HELPER'],
    recommendedRoles: ['HELPER'],
    unlockedCallables: ['raiseRequest'],
  },
  'm.plant': {
    key: 'm.plant',
    surface: 'MEMBER',
    labelKey: 'NLSOpsPhoneItemMemberPlant',
    descriptionKey: 'NLSOpsPhoneItemMemberPlantDesc',
    anyOfModules: ['plantEnabled'],
    allowedRoles: ['ADMIN', 'EDITOR', 'VIEWER', 'OWNER'],
    recommendedRoles: ['ADMIN', 'EDITOR', 'VIEWER', 'OWNER'],
    unlockedCallables: ['verifyRequest', 'releaseAsset', 'getPlantDowntimeReport'],
  },
}

export const OPS_PHONE_LEGACY_HELPER_GRANT: readonly OpsPhoneItemKey[] = [
  'h.checkIn',
  'h.roomClean',
  'h.foundItem',
  'h.bookingCheckIn',
  'h.request',
  'h.dueChecks',
  'h.roll',
] as const

export function isItemModuleActive(
  itemKey: OpsPhoneItemKey,
  modules?: Record<string, boolean | undefined> | null,
): boolean {
  if (!modules) return false
  const def = OPS_PHONE_ITEMS[itemKey]
  if (!def) return false
  return def.anyOfModules.some((flag) => Boolean(modules[flag]))
}

export function computeLegacyHelperGrant(
  modules?: Record<string, boolean | undefined> | null,
): OpsPhoneItemKey[] {
  if (!modules) return []
  return OPS_PHONE_LEGACY_HELPER_GRANT.filter((key) => isItemModuleActive(key, modules))
}

export const OPS_PHONE_RECOMMENDED_SETS: Record<WorkspaceRole, readonly OpsPhoneItemKey[]> = {
  ADMIN: [
    'm.approvals',
    'm.roster',
    'm.registers',
    'm.reports',
    'm.requests',
    'm.exceptions',
    'm.equipment',
    'm.beds',
    'm.bmw',
    'm.oncall',
    'm.dpr',
    'm.inspections',
    'm.plant',
  ],
  EDITOR: [
    'm.approvals',
    'm.roster',
    'm.registers',
    'm.reports',
    'm.requests',
    'm.exceptions',
    'm.equipment',
    'm.beds',
    'm.bmw',
    'm.oncall',
    'm.dpr',
    'm.inspections',
    'm.plant',
  ],
  VIEWER: ['m.roster', 'm.requests', 'm.oncall', 'm.plant'],
  READONLY: [],
  HELPER: [
    'h.checkIn',
    'h.dueChecks',
    'h.dpr',
    'h.hindrance',
    'h.roll',
    'h.request',
    'h.roomClean',
    'h.foundItem',
    'h.bookingCheckIn',
    'h.bmw',
    'h.oncall',
    'h.plant',
  ],
  OWNER: [
    'm.approvals',
    'm.roster',
    'm.registers',
    'm.reports',
    'm.requests',
    'm.exceptions',
    'm.equipment',
    'm.beds',
    'm.bmw',
    'm.oncall',
    'm.dpr',
    'm.inspections',
    'm.plant',
  ],
  LEARNER: [],
}

export interface ResolveOpsPhoneItemsInput {
  role: WorkspaceRole
  roleAllow?: Partial<Record<WorkspaceRole, readonly OpsPhoneItemKey[]>>
  person?: {
    allow?: readonly OpsPhoneItemKey[]
    deny?: readonly OpsPhoneItemKey[]
  } | null
  modules?: Record<string, boolean | undefined> | null
}

export function resolveOpsPhoneItems(input: ResolveOpsPhoneItemsInput): OpsPhoneItemKey[] {
  const { role, roleAllow, person, modules } = input
  const lookupRole: WorkspaceRole = role === 'OWNER' ? 'ADMIN' : role

  const roleSet = new Set<OpsPhoneItemKey>(roleAllow?.[lookupRole] ?? [])
  const personAllow = new Set<OpsPhoneItemKey>(person?.allow ?? [])
  const personDeny = new Set<OpsPhoneItemKey>(person?.deny ?? [])

  // (role allow ∪ person allow) minus person deny
  const candidateKeys = new Set<OpsPhoneItemKey>()
  for (const k of roleSet) candidateKeys.add(k)
  for (const k of personAllow) candidateKeys.add(k)
  for (const k of personDeny) candidateKeys.delete(k)

  const resolved: OpsPhoneItemKey[] = []
  for (const key of candidateKeys) {
    const item = OPS_PHONE_ITEMS[key]
    if (!item) continue

    // Role eligibility check: role outside item's allowedRoles never gets it
    const isAllowedForRole =
      item.allowedRoles.includes(role) ||
      (role === 'OWNER' && item.allowedRoles.includes('ADMIN'))
    if (!isAllowedForRole) continue

    // Module check: module off removes an item
    if (!isItemModuleActive(key, modules)) continue

    resolved.push(key)
  }

  return resolved
}
